import { useQuery } from '@tanstack/react-query';
import { TopBar } from '@/components/layout/TopBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import { Loader2, UserX } from 'lucide-react';
import { format } from 'date-fns';
import { Navigate } from 'react-router-dom';

/**
 * Platform Admin: Decoupled Users Directory.
 * Shows profiles with account_status='active' and zero active memberships.
 */
export default function DecoupledUsers() {
  const { data: isAdmin, isLoading: adminLoading } = useIsInternalAdmin();

  const { data: decoupledUsers, isLoading } = useQuery({
    queryKey: ['decoupled-users'],
    queryFn: async () => {
      // Get all profiles with active status
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, email, display_name, account_status')
        .eq('account_status', 'active');

      if (profilesError) throw profilesError;
      if (!profiles || profiles.length === 0) return [];

      // Get all user IDs that have active memberships
      const { data: activeMemberships, error: membError } = await supabase
        .from('memberships')
        .select('user_id')
        .eq('status', 'active');

      if (membError) throw membError;

      const activeUserIds = new Set((activeMemberships || []).map(m => m.user_id));

      // Filter to users without active memberships
      const decoupled = profiles.filter(p => !activeUserIds.has(p.id));

      // For each decoupled user, get their most recent removed membership
      const enriched = await Promise.all(
        decoupled.map(async (profile) => {
          const { data: lastMembership } = await supabase
            .from('memberships')
            .select('shop_id, role, removed_at')
            .eq('user_id', profile.id)
            .eq('status', 'removed')
            .order('removed_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          let shopName: string | null = null;
          if (lastMembership?.shop_id) {
            const { data: shop } = await supabase
              .from('shops')
              .select('name')
              .eq('id', lastMembership.shop_id)
              .maybeSingle();
            shopName = shop?.name || null;
          }

          return {
            ...profile,
            lastShopName: shopName,
            lastRole: lastMembership?.role || null,
            decoupledAt: lastMembership?.removed_at || null,
          };
        })
      );

      return enriched;
    },
    enabled: isAdmin === true,
  });

  if (adminLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <div className="container mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <UserX className="h-8 w-8" />
            Decoupled Users
          </h1>
          <p className="text-muted-foreground mt-1">
            Users with active accounts but no shop memberships
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Decoupled Users Directory</CardTitle>
            <CardDescription>
              {isLoading ? 'Loading...' : `${decoupledUsers?.length || 0} decoupled users found`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading decoupled users...
              </div>
            ) : !decoupledUsers || decoupledUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No decoupled users found
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Last Shop</TableHead>
                    <TableHead>Last Role</TableHead>
                    <TableHead>Decoupled</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {decoupledUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {user.display_name || 'Unknown'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {user.email || '—'}
                      </TableCell>
                      <TableCell>{user.lastShopName || '—'}</TableCell>
                      <TableCell>
                        {user.lastRole ? (
                          <Badge variant="secondary">{user.lastRole}</Badge>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {user.decoupledAt
                          ? format(new Date(user.decoupledAt), 'MMM d, yyyy')
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
